import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Pencil, Pin, Trash2, X } from 'lucide-react';
import { Button } from '@kop/ui';

import { getTrustedMediaAdapter } from '../media/trusted-media-adapter';
import { FeatureLoading } from '../feature-skeleton';
import {
  boardEmphasisLabel,
  displayCommand,
  postDate,
  releaseCommand,
  type BoardAttachment,
  type BoardEmphasis,
  type SafetyBoardAuthority,
  type SafetyBoardCommand,
  type SafetyBoardPost,
  type SafetyBoardReadReceipt,
  type SafetyBoardSettings,
} from '../safety-board/model';
import { createSafetyBoardRepository, safetyBoardFailureMessage } from '../safety-board/repository';
import { useSafetyBoardSite } from '../safety-board/site-context';
import { SafetyBoardCategoryChip } from '../safety-board/board-rows';

import './briefing.css';
import '../safety-board/safety-board.css';

const emphasisColor: Readonly<Record<BoardEmphasis, 'default' | 'warning' | 'critical'>> = {
  normal: 'default',
  important: 'warning',
  urgent: 'critical',
};

function BoardImageGallery({ attachments, siteId }: { attachments: readonly BoardAttachment[]; siteId: string | null }) {
  const [images, setImages] = useState<readonly Readonly<{ id: string; uri: string }>[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(0);
  useEffect(() => {
    let alive = true;
    const revokers: Array<() => void> = [];
    setImages([]);
    setLoading(true);
    setUnavailable(0);
    void Promise.allSettled(
      attachments.map(async (attachment) => {
        if (!siteId) throw new Error('SITE_SCOPE_REQUIRED');
        const resolved = await getTrustedMediaAdapter().resolveAuthoritativeDisplayAsset('kop', {
          siteId,
          manifestId: attachment.manifestId,
          evidenceVariantSha256: attachment.sha256,
        });
        if (alive) revokers.push(resolved.revoke);
        else resolved.revoke();
        return { id: attachment.manifestId, uri: resolved.uri };
      }),
    )
      .then((resolved) => {
        if (!alive) return;
        setImages(resolved.flatMap(result => result.status === 'fulfilled' ? [result.value] : []));
        setUnavailable(resolved.filter(result => result.status === 'rejected').length);
        setLoading(false);
      });
    return () => {
      alive = false;
      revokers.forEach((revoke) => revoke());
    };
  }, [attachments, siteId]);
  if (attachments.length === 0) return null;
  return (
    <section className="briefing-detail-gallery" aria-label="첨부 이미지">
      {loading ? (
        <FeatureLoading label="첨부 사진 불러오는 중" />
      ) : (
        images.map((image, index) => (
          <a key={image.id} href={image.uri} target="_blank" rel="noreferrer">
            <img src={image.uri} alt={`게시글 첨부 이미지 ${index + 1}`} />
          </a>
        ))
      )}
      {unavailable > 0 && <p role="status">첨부 사진 {unavailable}장을 불러오지 못했습니다.</p>}
    </section>
  );
}

export interface BoardDetailPanelProps {
  post: SafetyBoardPost;
  authority: SafetyBoardAuthority;
  onClose(): void;
  onReadReceipt(receipt: SafetyBoardReadReceipt): void;
  onCommand(command: SafetyBoardCommand): Promise<void>;
  onEdit?: (post: SafetyBoardPost) => void;
  settings?: SafetyBoardSettings | null;
}

export function BoardDetailPanel({
  post,
  authority,
  onClose,
  onReadReceipt,
  onCommand,
  onEdit,
  settings = null,
}: BoardDetailPanelProps) {
  const siteId = useSafetyBoardSite();
  const panelRef = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const host = document.querySelector<HTMLElement>('.workspace-shell') ?? document.body;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector<HTMLElement>('button')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
      if (event.key === 'Tab') {
        const controls = [...(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? [])];
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keydown, true);
    return () => {
      document.removeEventListener('keydown', keydown, true);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    void createSafetyBoardRepository(siteId)
      .then((repository) => repository.markRead(post.boardId))
      .then((receipt) => {
        if (alive) onReadReceipt(receipt);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [onReadReceipt, post.boardId, siteId]);

  const run = async (command: SafetyBoardCommand) => {
    setBusy(true);
    setFailure('');
    try {
      await onCommand(command);
      if (command.action === 'delete') onClose();
    } catch (error) {
      setFailure(safetyBoardFailureMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="briefing-detail-layer" onMouseDown={onClose}>
      <article
        ref={panelRef}
        className="briefing-detail-panel glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="briefing-detail-title"
        data-emphasis={post.emphasis}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <SafetyBoardCategoryChip post={post} categories={settings?.categories ?? []} />
          <div className="briefing-detail-head-actions">
            {authority.canRelease && (
              <div className="briefing-manage-menu">
                <button
                  type="button"
                  aria-label="게시 관리"
                  title="게시 관리"
                  aria-expanded={manageOpen}
                  onClick={() => setManageOpen((value) => !value)}
                >
                  <MoreHorizontal size={16} />
                </button>
                {manageOpen && (
                  <div role="menu">
                    {onEdit && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={busy}
                        onClick={() => onEdit(post)}
                      >
                        <Pencil size={14} /> 수정
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      disabled={busy}
                      onClick={() => void run(displayCommand(post, { pinned: !post.pinned }))}
                    >
                      <Pin size={14} /> {post.pinned ? '고정 해제' : '상단 고정'}
                    </button>
                    <fieldset>
                      <legend>강조</legend>
                      {(['normal', 'important', 'urgent'] as const).map((value) => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={post.emphasis === value}
                          disabled={busy}
                          onClick={() =>
                            void run(displayCommand(post, { titleColor: emphasisColor[value] }))
                          }
                        >
                          {boardEmphasisLabel[value]}
                        </button>
                      ))}
                    </fieldset>
                    <button
                      type="button"
                      role="menuitem"
                      className="danger"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`“${post.title}” 게시글을 삭제할까요?`))
                          void run(releaseCommand('delete', post));
                      }}
                    >
                      <Trash2 size={14} /> 삭제
                    </button>
                  </div>
                )}
              </div>
            )}
            <Button variant="ghost" type="button" aria-label="게시글 닫기" title="게시글 닫기" onClick={onClose} icon={<X size={16} />} />
          </div>
        </header>
        <div className="briefing-detail-content" data-bounded-content>
          <div className="briefing-detail-kicker">
            <span>{post.pinned ? '상단 고정' : boardEmphasisLabel[post.emphasis]}</span>
            <time>{postDate(post)}</time>
          </div>
          <h2 id="briefing-detail-title">{post.title}</h2>
          <p className="briefing-detail-body">{post.body}</p>
          <BoardImageGallery key={JSON.stringify([siteId, post.boardId, post.attachments])} siteId={siteId} attachments={post.attachments} />
          {failure && (
            <p className="briefing-detail-message" data-tone="danger" role="alert">
              {failure}
            </p>
          )}
        </div>
      </article>
    </div>,
    host,
  );
}
